/**
 * Lazy, per-group access to the consultative depth registry (~660 KB of copy).
 *
 * Importing `./depth` statically pulls DEPTH_A..DEPTH_L into the consuming
 * chunk (~100 KB gzip). Route loaders call `loadPillarDepth()` instead, which
 * resolves the slug through a tiny generated index and imports only the one
 * group module that actually holds it (~5–10 KB gzip).
 */
import type { PillarDepth } from "./depth-shared";
import { DEPTH_GROUP_BY_SLUG, type DepthGroup } from "./depth-index.gen";

const LOADERS: Record<DepthGroup, () => Promise<Record<string, PillarDepth>>> = {
  a: async () => (await import("./depth-a")).DEPTH_A,
  b: async () => (await import("./depth-b")).DEPTH_B,
  c: async () => (await import("./depth-c")).DEPTH_C,
  d: async () => (await import("./depth-d")).DEPTH_D,
  e: async () => (await import("./depth-e")).DEPTH_E,
  f: async () => (await import("./depth-f")).DEPTH_F,
  g: async () => (await import("./depth-g")).DEPTH_G,
  h: async () => (await import("./depth-h")).DEPTH_H,
  i: async () => (await import("./depth-i")).DEPTH_I,
  j: async () => (await import("./depth-j")).DEPTH_J,
  k: async () => (await import("./depth-k")).DEPTH_K,
  l: async () => (await import("./depth-l")).DEPTH_L,
};

export async function loadPillarDepth(slug: string): Promise<PillarDepth | null> {
  const group = DEPTH_GROUP_BY_SLUG[slug];
  if (!group) return null;
  const bundle = await LOADERS[group]();
  return bundle[slug] ?? null;
}

export type { PillarDepth };
